import { useState, useEffect } from "react";
import { Avatar, Badge, Box, Fab, InputAdornment, TextField, Tooltip, Typography, Skeleton } from "@mui/material";
import { SearchIcon } from "../../../assets/CommonSvg";
import { AddGroupIcon } from "../../../assets/CommonSvg";
import { useSelector } from "react-redux";
import { selectAllChatRoom, selectChatLoading, selectCountUnreadMessege } from "../../../slices/chat";

const InboxChat = ({ setChatView, setSelectedRoom, disableAdd }) => {
    const allChatRoom = useSelector(selectAllChatRoom)
    const isLoading = useSelector(selectChatLoading)
    const [searchTerm, setSearchTerm] = useState("");
    const [items, setItems] = useState([]); // Store all loaded items
    const countUnreadMessege = useSelector(selectCountUnreadMessege)

    // Fetch initial data
    useEffect(() => {
        const updatedItems = allChatRoom.map(item => {
            const unreadData = countUnreadMessege?.find(d => d.chat_room_id === item.id);
            return unreadData ? { ...item, unread_count: unreadData.unread_count } : item;
        });
        setItems(updatedItems);
    }, [countUnreadMessege, allChatRoom]);

    // Filter items based on search term
    const filteredItems = items.filter(item =>
        item?.patient_id.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        item.name.toLowerCase().includes(searchTerm?.toLowerCase()) 
    );
    
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const renderSkeleton = () => {
        return Array(5).fill(0).map((_, index) => (
            <div key={`skeleton-${index}`} className='pt-3 pb-3 border-b flex gap-2 items-center justify-between'>
                <div className='flex gap-2 items-center w-full'>
                    <Skeleton variant="circular" width={40} height={40} />
                    <div className="w-full">
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="text" width="40%" height={15} />
                    </div>
                </div>
                <Skeleton variant="circular" width={20} height={20} className="mr-3" />
            </div>
        ));
    };

    return (
        <div className="pl-3 pr-3 pb-3 flex flex-col h-full" style={{ overflow: 'hidden' }}>
            <Box className="flex mt-2 items-center justify-between">
                <Box>
                    <TextField
                        variant="outlined"
                        className="bg-[#F0F1F5] w-[17rem] py-[2.5px] border-0 rounded-[10px]"
                        size="small"
                        placeholder={"Search By Patient's Name and ID"}
                        sx={{ "& fieldset": { border: 'none' } }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon className="text-gray-500" />
                                </InputAdornment>
                            )
                        }}
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                </Box>
                {!disableAdd &&
                    <Tooltip title="Add New Group" arrow placement="top">
                        <Box className="cursor-pointer" onClick={() => setChatView("group")}>
                            <AddGroupIcon width="30" height="30" />
                        </Box>
                    </Tooltip>}
            </Box>

            <div id="scrollableDiv" className="flex-1 overflow-y-auto bg-white">
                {isLoading && filteredItems.length === 0 && renderSkeleton()}
                {filteredItems.length === 0 && items.length > 0 ? (
                    <div className="flex items-center justify-center h-full bg-white">
                        <div className="text-center">
                            <h2 className="mt-4 text-lg font-semibold text-black">No rooms found</h2>
                        </div>
                    </div>
                ) : filteredItems.length === 0 && !isLoading && !disableAdd ? (
                    <div className="flex items-center justify-center mt-[6rem] bg-white">
                        <div className="text-center">
                            <Fab className="!bg-[#3571ff] hover:!bg-[#3571ff]" onClick={() => setChatView("group")}>
                                <span className="text-white">
                                    <AddGroupIcon />
                                </span>
                            </Fab>
                            <h2 className="mt-4 text-lg font-semibold text-black">Start New Group</h2>
                        </div>
                    </div>
                ) : (
                    filteredItems.map((room, index) => (
                        <div
                            key={`${room.id}-${index}`}
                            className='pt-3 pb-3 border-b flex gap-2 items-center justify-between cursor-pointer'
                            style={{ minHeight: '72px' }}
                            onClick={() => { setChatView("chat"), setSelectedRoom(room) }}
                        >
                            <div className='flex gap-2 items-center w-full overflow-hidden'>
                                <Avatar className='bg-[#F0F1F5] text-[#828282] text-[0.8rem] min-w-[40px] capitalize'>
                                    {room.name[0]}{room.name[1]}
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <Typography
                                        variant="h6"
                                        className='text-black text-[0.9rem] font-medium whitespace-nowrap overflow-hidden text-ellipsis'
                                        title={`${room.name}`}
                                    >
                                        {room.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className='text-[#828282] text-[0.8rem] whitespace-nowrap overflow-hidden text-ellipsis'
                                        title={room.patient_id}
                                    >
                                        {room.patient_id}
                                    </Typography>
                                </div>
                            </div>
                            {room.unread_count > 0 && (
                                <Badge
                                    color="info"
                                    badgeContent={room.unread_count}
                                    max={99}
                                    className="mr-[0.9rem]"
                                />
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default InboxChat;